import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type {
  AppTemplateRow,
  CertificateRow,
  CertificationRow,
  ContactMessageRow,
  DesignRow,
  EducationRow,
  ExperienceRow,
  ExtensionRow,
  MobileAppRow,
  OrderRow,
  ProjectRow,
  ServiceRow,
  SiteSettingsRow,
  SkillRow,
  TableName,
  VideoRow,
  WebsiteRow,
} from '@/types/database';

export function useTableQuery<T>(table: TableName) {
  return useQuery({
    queryKey: [table],
    queryFn: async (): Promise<T[]> => {
      const { data, error } = await supabase.from(table).select('*').order('sort_order', { ascending: true });
      if (error) throw error;
      return data as T[];
    },
  });
}

export const useSkills = () => useTableQuery<SkillRow>('skills');
export const useServices = () => useTableQuery<ServiceRow>('services');
export const useWebsites = () => useTableQuery<WebsiteRow>('websites');
export const useExtensions = () => useTableQuery<ExtensionRow>('extensions');
export const useProjects = () => useTableQuery<ProjectRow>('projects');
export const useCertificates = () => useTableQuery<CertificateRow>('certificates');
export const useDesigns = () => useTableQuery<DesignRow>('designs');
export const useAppTemplates = () => useTableQuery<AppTemplateRow>('app_templates');
export const useMobileApps = () => useTableQuery<MobileAppRow>('mobile_apps');
export const useExperiences = () => useTableQuery<ExperienceRow>('experiences');
export const useEducation = () => useTableQuery<EducationRow>('education');
export const useCertifications = () => useTableQuery<CertificationRow>('certifications');
export const useVideos = () => useTableQuery<VideoRow>('videos');

export function useCreateRow<T extends Record<string, unknown>>(table: TableName) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (row: Partial<T>) => {
      const { data, error } = await supabase.from(table).insert(row as never).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [table] }),
  });
}

export function useUpdateRow<T extends Record<string, unknown>>(table: TableName) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...row }: Partial<T> & { id: string }) => {
      const { data, error } = await supabase.from(table).update(row as never).eq('id', id).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [table] }),
  });
}

export function useDeleteRow(table: TableName) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from(table).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [table] }),
  });
}

// Orders aren't reorderable editorial content like the tables above — no
// sort_order, no admin "Add" flow (buyers create them from the public
// checkout form) — so they get their own small set of hooks instead of
// going through the generic TableName machinery.
export function useOrders() {
  return useQuery({
    queryKey: ['orders'],
    queryFn: async (): Promise<OrderRow[]> => {
      const { data, error } = await supabase.from('orders').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      return data as OrderRow[];
    },
  });
}

export function useCreateOrder() {
  return useMutation({
    mutationFn: async (order: Omit<OrderRow, 'id' | 'status' | 'created_at'>) => {
      // No `.select()` — the public insert policy intentionally has no
      // matching select policy (buyers shouldn't be able to read orders
      // back), so requesting a returned row would fail even though the
      // insert itself succeeds.
      const { error } = await supabase.from('orders').insert(order as never);
      if (error) throw error;
    },
  });
}

export function useUpdateOrderStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: OrderRow['status'] }) => {
      const { error } = await supabase.from('orders').update({ status }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['orders'] }),
  });
}

export function useDeleteOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('orders').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['orders'] }),
  });
}

// Messages from the homepage contact form. Same reasoning as orders: visitors
// can insert (no `.select()` — the insert policy has no matching select
// policy, so asking for the row back would fail even though the insert
// succeeds) but only the signed-in admin can read, mark read, or delete.
export function useContactMessages() {
  return useQuery({
    queryKey: ['contact_messages'],
    queryFn: async (): Promise<ContactMessageRow[]> => {
      const { data, error } = await supabase
        .from('contact_messages')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as ContactMessageRow[];
    },
  });
}

export function useCreateContactMessage() {
  return useMutation({
    mutationFn: async (message: Pick<ContactMessageRow, 'name' | 'email' | 'message'>) => {
      const { error } = await supabase.from('contact_messages').insert(message as never);
      if (error) throw error;
    },
  });
}

export function useSetContactMessageRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, is_read }: { id: string; is_read: boolean }) => {
      const { error } = await supabase.from('contact_messages').update({ is_read }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contact_messages'] }),
  });
}

export function useDeleteContactMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('contact_messages').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contact_messages'] }),
  });
}

// Single-row config table (EasyPaisa number, WhatsApp number, etc.) — not a
// list of editorial entries, so it gets its own read/update pair instead of
// the generic list CRUD above.
export function useSiteSettings() {
  return useQuery({
    queryKey: ['site_settings'],
    queryFn: async (): Promise<SiteSettingsRow> => {
      const { data, error } = await supabase.from('site_settings').select('*').eq('id', 1).single();
      if (error) throw error;
      return data as SiteSettingsRow;
    },
  });
}

export function useUpdateSiteSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (settings: Partial<Omit<SiteSettingsRow, 'id' | 'updated_at'>>) => {
      const { error } = await supabase
        .from('site_settings')
        .update({ ...settings, updated_at: new Date().toISOString() })
        .eq('id', 1);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['site_settings'] }),
  });
}

export function useReorderRow(table: TableName) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, sort_order }: { id: string; sort_order: number }) => {
      const { error } = await supabase.from(table).update({ sort_order }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [table] }),
  });
}
